import { z } from 'zod'

import type { LogGeo } from '@/shared/geoip/types'
import type { EventSchemaResponse } from '@/shared/opensearch/types/schema'
import type { RuleGroupType } from 'react-querybuilder'

/**
 * Log source selection: `id` = definition id (a connector's or a network log
 * source's), `ev` = event type id.
 */
export const searchIntegrationsSchema = z.array(
	z.object({
		id: z.string(),
		ev: z.string(),
	}),
)

const timeRangePresetSchema = z.enum([
	'last-15m',
	'last-1h',
	'last-24h',
	'last-7d',
	'last-30d',
])

const absoluteTimeRangeSchema = z.object({
	st: z.iso.datetime({ offset: true }),
	ed: z.iso.datetime({ offset: true }),
})

const presetTimeRangeSchema = z.object({
	preset: timeRangePresetSchema,
})

/**
 * Time range — either a rolling preset (resolves to absolute at render) or
 * an explicit absolute window. ISO 8601 UTC strings ending in 'Z'.
 */
export const searchTimeRangeSchema = z.union([
	presetTimeRangeSchema,
	absoluteTimeRangeSchema,
])

export const filterOperatorSchema = z.enum([
	'eq',
	'neq',
	'in',
	'not_in',
	'gt',
	'lt',
	'gte',
	'lte',
	'contains',
	'exists',
])

/**
 * Structured filter. Lives inside `ft` URL entries.
 * - `f` = canonical field id (e.g. `event.userName`, `normalized_fields.username`)
 * - `o` = operator
 * - `v` = single value or array (for `in` / `not_in`)
 */
export const structuredFilterSchema = z.object({
	f: z.string(),
	o: filterOperatorSchema,
	v: z.union([z.string(), z.array(z.string())]),
})

/**
 * Zod Schema for the Search page URL params. Short keys keep the URL terse.
 *
 * Breaking changes from v1: `q` is now a native object (not JSON.stringify'd),
 * `ft` is structured, `tr` accepts presets, `cid` removed.
 *
 * `q` uses `z.custom<RuleGroupType>()` instead of `z.any()` so consumers get
 * full RuleGroupType inference without the runtime validation overhead of a
 * deep schema (RQB's shape is recursive and large).
 */
export const searchSchema = z.object({
	int: searchIntegrationsSchema.optional(),
	q: z.custom<RuleGroupType>().optional(),
	// An invalid `tr` drops to `undefined` rather than failing the whole URL;
	// the route's `beforeLoad` then fills the default window.
	tr: searchTimeRangeSchema.optional().catch(undefined),
	// Same leniency, same reason: a URL is pasted, bookmarked and rebuilt by
	// Noah's chat links, so a chip written by an older build — an operator since
	// dropped from `filterOperatorSchema` — must cost the filters, not the whole
	// page. Without this the route throws and the analyst gets a blank screen
	// (P-17). Coarser than `tr`: one bad chip drops the list, since a partial
	// filter set would narrow the results in a way the URL no longer describes.
	ft: z.array(structuredFilterSchema).optional().catch(undefined),
})

export type SearchParams = z.infer<typeof searchSchema>

export type SearchIntegrations = NonNullable<
	z.infer<typeof searchIntegrationsSchema>
>

export type PrettyfiedSearchParams = Omit<SearchParams, 'int'> & {
	integrations: Array<string>
	events: Array<string>
}

export type SearchTimeRange = z.infer<typeof searchTimeRangeSchema>
export type SearchFilter = z.infer<typeof structuredFilterSchema>
export type FilterOperator = z.infer<typeof filterOperatorSchema>

/**
 * Saved search payload format. The `version: 2` discriminator lets us safely
 * ignore legacy v1 rows (decodeSavedSearch returns null when version !== 2).
 */
export const savedSearchPayloadSchema = z.object({
	version: z.literal(2),
	int: searchIntegrationsSchema.optional(),
	// Same leniency as the URL: a bad `tr` must not drop the whole saved row.
	tr: searchTimeRangeSchema.optional().catch(undefined),
	q: z.custom<RuleGroupType>().optional(),
	// Same again for `ft`, mirroring `searchSchema`: `decodeSavedSearch`
	// safe-parses the row, so one chip with a retired operator would otherwise
	// null the whole saved search and drop it from the list.
	ft: z.array(structuredFilterSchema).optional().catch(undefined),
})

export type SavedSearchPayload = z.infer<typeof savedSearchPayloadSchema>

export type QuickSelectUnit = 'second' | 'minute' | 'hour' | 'day' | 'week'

export type EventTypeSelection = {
	definitionId: string
	eventType: string
}

export type DefinitionOption = {
	value: string
	label: string
}

export type EventTypeOption = {
	value: string
	label: string
	definitionId: string
	definitionName: string
}

export type SavedSearchResponse = {
	saved_searches: Array<SavedSearch>
}

export type SavedSearch = {
	tenant: string
	name: string
	created_by_username: string
	updated_by_username: string
	created_at: string
	updated_at: string
	search_string: string
}

/** The `/bql/fields` error vocabulary (air-backend `QueryError`). */
export type LogSchemaError = {
	code: 'forbidden' | 'feature_not_entitled' | 'validation' | 'execution'
	message: string
	/** Sent without `null`s, so an unset field is usually absent. */
	field?: string | null
}

/**
 * One `logs/schemas` entry (air-backend `SchemaSectionSuccess` /
 * `SchemaSectionFailure`). `source` echoes its `definition_id:event_type` key.
 */
export type LogSchemaSection =
	| ({ source: string; success: true } & Required<EventSchemaResponse>)
	| { source: string; success: false; error: LogSchemaError }

/**
 * `GET logs/schemas`, keyed `definition_id:event_type`. A requested pair the
 * tenant's `logs/sources` does not list is absent, not a failure.
 */
export type LogSchemasResponse = {
	sources: Partial<Record<string, LogSchemaSection>>
}

/**
 * One search hit. `fields` is the flat `_source.event` object plus `_id` and
 * `@timestamp`; `geo` is the GeoIP enrichment from `normalized_fields`, kept
 * apart on purpose. Everything in `fields` is treated as event-derived:
 * `canonicalizeField` prefixes its keys with `event.`, so a
 * `normalized_fields` key merged into `fields` would filter on the wrong
 * field. It would also leak into the Table tab, summary badges and field count.
 */
export type SearchResultHit = {
	fields: Record<string, unknown>
	geo: LogGeo | undefined
}
